import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as announcementsApi from '../../api/announcements';
import { formatDateTime } from '../../utils/format';

const CATEGORY_OPTIONS = ['Company Update', 'Meeting Notice', 'Policy Update', 'General Notice'].map((v) => ({ value: v, label: v }));

export default function Announcements() {
  const { actor } = useAuth();
  const canPost = actor.role === 'super_admin' || actor.role === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'General Notice' });

  const load = useCallback(() => {
    setLoading(true);
    announcementsApi
      .listAnnouncements({})
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load announcements.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await announcementsApi.createAnnouncement(form);
      setSuccess('Announcement published.');
      setShowForm(false);
      setForm({ title: '', content: '', category: 'General Notice' });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to publish announcement.');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await announcementsApi.deleteAnnouncement(row.id);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete announcement.');
    }
  };

  return (
    <DashboardLayout title="Announcements">
      <div className="page-header">
        <div>
          <h1 className="heading">Announcements</h1>
          <p className="page-subtext">Company-wide updates visible to everyone.</p>
        </div>
        {canPost && <button type="button" className="btn" onClick={() => setShowForm(true)}>New Announcement</button>}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      {loading ? (
        <Loader text="Loading announcements..." />
      ) : rows.length === 0 ? (
        <p className="text-muted">No announcements yet.</p>
      ) : (
        rows.map((a) => (
          <div key={a.id} className="card">
            <div className="flex-between">
              <h3 className="subheading mb-0">{a.title}</h3>
              {canPost && <button type="button" className="btn-link" onClick={() => handleDelete(a)}>Delete</button>}
            </div>
            <div className="text-muted" style={{ fontSize: '9.5pt', margin: '4px 0 10px' }}>
              {a.category} &middot; {a.author?.name} &middot; {formatDateTime(a.createdAt)}
            </div>
            <p>{a.content}</p>
          </div>
        ))
      )}

      {showForm && (
        <Modal
          title="New Announcement"
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="announcement-form" className="btn">Publish</button>
            </>
          }
        >
          <form id="announcement-form" onSubmit={handleCreate}>
            <div className="form-grid single-column">
              <FormField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              <FormField label="Category" type="select" options={CATEGORY_OPTIONS} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <FormField label="Content" type="textarea" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required />
            </div>
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}
